/**
 * Utility functions for infrastructure and server monitoring.
 */

/**
 * Creates a normalized API Gateway server object from system stats.
 */
export function createApiGatewayServer(apiGateway, latencyMs, t) {
  if (!apiGateway) {
    return {
      id: "srv-001",
      name: "API Gateway",
      type: "gateway",
      region: "Singapore",
      ip: "",
      status: "offline",
      isDataReal: false,
      uptime: 0,
      cpu: 0,
      memory: 0,
      disk: 0,
      network: { in: 0, out: 0 },
      requests: 0,
      latency: 0,
    };
  }
  return {
    id: "srv-001",
    name: "API Gateway",
    type: "gateway",
    region: "Singapore",
    ip: "",
    status: apiGateway.status === "healthy" ? "healthy" : apiGateway.status === "degraded" ? "warning" : "critical",
    isDataReal: true,
    uptime: apiGateway.uptimePercentage ?? 0,
    cpu: apiGateway.cpu ?? 0,
    memory: apiGateway.memory ?? 0,
    disk: apiGateway.disk ?? 0,
    network: {
      in: Number.isFinite(Number(apiGateway.networkIn)) ? Number(apiGateway.networkIn) : null,
      out: Number.isFinite(Number(apiGateway.networkOut)) ? Number(apiGateway.networkOut) : null,
    },
    requests: 0,
    latency: latencyMs ?? apiGateway.latencyMs ?? 0,
  };
}

/**
 * Merges real-time server data with mock/static data.
 */
export function mergeServersData(mockServers, systemStats, apiGw, bankserverHealth, bankserverLogs) {
  const list = [...mockServers];
  const hasApiGw = list.some((s) => s.name === "API Gateway");
  if (!hasApiGw && apiGw) list.unshift(apiGw);
  
  return list.map((s) => {
    if (s.name === "Database Primary" && systemStats?.database) {
      return {
        ...s,
        status: systemStats.database.status === "healthy" ? "healthy" : systemStats.database.status === "degraded" ? "warning" : "critical",
        isDataReal: true,
        uptime: systemStats.database.uptimePercentage ?? s.uptime,
        cpu: systemStats.database.cpu ?? s.cpu,
        memory: systemStats.database.memory ?? s.memory,
        disk: systemStats.database.disk ?? s.disk,
        network: {
          in: Number.isFinite(Number(systemStats.database.networkIn))
            ? Number(systemStats.database.networkIn)
            : null,
          out: Number.isFinite(Number(systemStats.database.networkOut))
            ? Number(systemStats.database.networkOut)
            : null,
        },
        latency: systemStats.database.latencyMs ?? s.latency,
      };
    }
    if (s.name === "Payment Processor" && bankserverHealth) {
      const logs = bankserverLogs?.logs || [];
      const upCount = logs.filter((log) => (log?.status || "").toUpperCase() === "UP").length;
      const uptimePercentage = logs.length > 0 ? Number(((upCount / logs.length) * 100).toFixed(2)) : (s.uptime || 0);
      const latestLatency = logs.length > 0 ? (logs[0]?.latencyMs ?? 0) : (bankserverHealth.latency ?? 0);

      // Determine status based on metrics
      let status = "healthy";
      if (bankserverHealth.memory > 85 || bankserverHealth.cpu > 80) {
        status = "warning";
      }
      if (bankserverHealth.memory > 95 || bankserverHealth.cpu > 90) {
        status = "critical";
      }
      return {
        ...s,
        status,
        isDataReal: true,
        uptime: uptimePercentage,
        cpu: bankserverHealth.cpu ?? s.cpu,
        memory: bankserverHealth.memory ?? s.memory,
        disk: bankserverHealth.disk ?? s.disk,
        network: {
          in: Number.isFinite(Number(bankserverHealth.networkIn))
            ? Number(bankserverHealth.networkIn)
            : null,
          out: Number.isFinite(Number(bankserverHealth.networkOut))
            ? Number(bankserverHealth.networkOut)
            : null,
        },
        latency: latestLatency,
      };
    }
    return s;
  });
}

/**
 * Resolves a time range string to milliseconds.
 */
export function resolveRangeMs(range) {
  switch (range) {
    case "1h":
      return 60 * 60 * 1000;
    case "6h":
      return 6 * 60 * 60 * 1000;
    case "24h":
      return 24 * 60 * 60 * 1000;
    case "7d":
      return 7 * 24 * 60 * 60 * 1000;
    default:
      return 24 * 60 * 60 * 1000;
  }
}

/**
 * Gets the appropriate bucket size for chart data based on the time range.
 */
export function getBucketSizeMs(range) {
  switch (range) {
    case "1h":
      return 60 * 1000;
    case "6h":
      return 5 * 60 * 1000;
    case "24h":
      return 15 * 60 * 1000;
    case "7d":
      return 60 * 60 * 1000;
    default:
      return 15 * 60 * 1000;
  }
}
